/**
 * Marked-PDF export — rasterizes selected sheets with their takeoffs,
 * highlights and search marks burned in, then stitches a new PDF with
 * pdf-lib (preserving each page's orientation).
 */
import { PDFDocument } from "pdf-lib";
import { renderPageToCanvas } from "./pdfSetup";

const TARGET_MIN = 1800;
const TARGET_MAX = 3200;

/** Draw a takeoff/highlight set onto a 2d canvas. Geometry is in the
 * normalized 1000-unit space of the (rotated) page. */
export function drawMarks(ctx, { pageW, pageH, unitH, items, highlights, searchBoxes }) {
  const sx = pageW / 1000;
  const sy = pageH / unitH;
  const lw = Math.max(2, 4 * ((sx + sy) / 2) * (pageW / 1600)); // ~4px at typical export zoom
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const hb of searchBoxes || []) {
    ctx.fillStyle = "rgba(250, 204, 21, 0.35)";
    ctx.fillRect(hb.x * sx, hb.y * sy, hb.w * sx, hb.h * sy);
  }
  for (const hgl of highlights || []) {
    ctx.fillStyle = "rgba(250, 204, 21, 0.3)";
    ctx.fillRect(hgl.x * sx, hgl.y * sy, hgl.width * sx, hgl.height * sy);
  }
  for (const it of items || []) {
    ctx.strokeStyle = it.color || "#2563eb";
    ctx.fillStyle = it.color || "#2563eb";
    ctx.lineWidth = lw;
    const pts = it.points || [];
    if (it.type === "linear" && pts.length > 1) {
      ctx.beginPath();
      ctx.moveTo(pts[0].x * sx, pts[0].y * sy);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * sx, pts[i].y * sy);
      ctx.stroke();
    } else if (it.type === "area" && pts.length > 2) {
      ctx.beginPath();
      ctx.moveTo(pts[0].x * sx, pts[0].y * sy);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * sx, pts[i].y * sy);
      ctx.closePath();
      ctx.globalAlpha = 0.22;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.stroke();
    } else if (it.type === "count" && pts.length) {
      const r = Math.max(8, lw * 2.2);
      const cx = pts[0].x * sx, cy = pts[0].y * sy;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = Math.max(2, lw * 0.6);
      ctx.beginPath();
      ctx.moveTo(cx - r * 0.5, cy); ctx.lineTo(cx + r * 0.5, cy);
      ctx.moveTo(cx, cy - r * 0.5); ctx.lineTo(cx, cy + r * 0.5);
      ctx.stroke();
    }
  }
}

/**
 * Build the marked PDF. `pages` = [{pageNum, rotation, unitH, items,
 * highlights, searchBoxes}] — each sheet keeps its own rotation and aspect.
 * onProgress(i, total) per page. Returns {blob, filename} named
 * `<source>-marked-pages.pdf`.
 */
export async function buildMarkedPdf(doc, defaultRotation, defaultUnitH, pages, sourceName, onProgress) {
  const out = await PDFDocument.create();
  for (let i = 0; i < pages.length; i++) {
    const p = pages[i];
    onProgress?.(i, pages.length);
    const rotation = p.rotation ?? defaultRotation;
    const unitH = p.unitH ?? defaultUnitH;
    let target = 2200;
    const { canvas, width, height } = await renderPageToCanvas(doc, p.pageNum, rotation, target);
    const ctx = canvas.getContext("2d");
    drawMarks(ctx, { pageW: width, pageH: height, unitH, items: p.items, highlights: p.highlights, searchBoxes: p.searchBoxes });
    const png = await new Promise((res) => canvas.toBlob(res, "image/png"));
    const img = await out.embedPng(await png.arrayBuffer());
    const page = out.addPage([width, height]);
    page.drawImage(img, { x: 0, y: 0, width, height });
  }
  onProgress?.(pages.length, pages.length);
  const bytes = await out.save();
  const base = (sourceName || "drawing").replace(/\.pdf$/i, "");
  return { blob: new Blob([bytes], { type: "application/pdf" }), filename: `${base}-marked-pages.pdf` };
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
